import {
  detectImageFormat,
  MAX_IMAGE_BYTES,
  IMAGE_DOWNLOAD_TIMEOUT_MS,
} from './image-format'

const IMAGE_DECODE_TIMEOUT_MS = 8000
const MAX_CANVAS_SIDE = 4096

export async function loadWordImage(source: string) {
  let bytes: Uint8Array<ArrayBuffer>
  if (source.startsWith('data:')) {
    const match = source.match(/^data:image\/[a-z0-9.+-]+;base64,([\s\S]+)$/i)
    if (!match) throw new Error('Unsupported inline image')
    bytes = Uint8Array.from(atob(match[1]), (character) =>
      character.charCodeAt(0),
    )
  } else {
    const target = new URL(source, window.location.href)
    if (!['http:', 'https:'].includes(target.protocol))
      throw new Error('Unsupported image URL')
    const controller = new AbortController()
    // 客户端额外留出网络往返时间，超时覆盖响应体读取。
    const timer = setTimeout(
      () => controller.abort(),
      IMAGE_DOWNLOAD_TIMEOUT_MS + 5000,
    )
    try {
      const response = await fetch(
        `/api/image-proxy?url=${encodeURIComponent(target.toString())}`,
        { signal: controller.signal },
      )
      if (!response.ok) throw new Error('Image download failed')
      const reader = response.body?.getReader()
      if (!reader) throw new Error('Missing image body')
      const chunks: Uint8Array[] = []
      let length = 0
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        length += value.length
        if (length > MAX_IMAGE_BYTES) {
          await reader.cancel()
          throw new Error('Image exceeds size limit')
        }
        chunks.push(value)
      }
      bytes = new Uint8Array(length)
      let offset = 0
      for (const chunk of chunks) {
        bytes.set(chunk, offset)
        offset += chunk.length
      }
    } finally {
      clearTimeout(timer)
    }
  }

  const format = detectImageFormat(bytes)
  if (!format) throw new Error('Unsupported image data')
  const blobUrl = URL.createObjectURL(new Blob([bytes], { type: format.mime }))
  const image = new Image()
  try {
    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        image.onload = null
        image.onerror = null
        image.src = ''
        reject(new Error('Image decoding timed out'))
      }, IMAGE_DECODE_TIMEOUT_MS)
      image.onload = () => {
        clearTimeout(timer)
        resolve()
      }
      image.onerror = () => {
        clearTimeout(timer)
        reject(new Error('Image decoding failed'))
      }
      image.src = blobUrl
    })
    const width = image.naturalWidth
    const height = image.naturalHeight
    if (!width || !height) throw new Error('Invalid image dimensions')
    if (format.wordType)
      return { data: bytes, type: format.wordType, width, height }

    // WebP、AVIF、ICO、SVG 转为 PNG 后写入 Word，避免把未知格式误标为 PNG。
    const scale = Math.min(1, MAX_CANVAS_SIDE / Math.max(width, height))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(width * scale))
    canvas.height = Math.max(1, Math.round(height * scale))
    const context = canvas.getContext('2d')
    if (!context) throw new Error('Image conversion is unavailable')
    context.drawImage(image, 0, 0, canvas.width, canvas.height)
    const png = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (blob) =>
          blob ? resolve(blob) : reject(new Error('Image conversion failed')),
        'image/png',
      ),
    )
    return {
      data: new Uint8Array(await png.arrayBuffer()),
      type: 'png' as const,
      width,
      height,
    }
  } finally {
    image.onload = null
    image.onerror = null
    URL.revokeObjectURL(blobUrl)
  }
}
